<template>
    <div class="main-content">
        <!-- TOASTS DE NOTIFICAÇÃO -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Gestão da Plataforma</li>
                </ol>
            </nav>
        </header>

        <!-- HEADER PRINCIPAL E ABAS NO ESTILO DA CAPTURA DE TELA -->
        <div class="card p-4 shadow-subtle rounded-4 border-0 mb-4">
            <div class="mb-3">
                <h4 class="mb-1 fw-bold text-dark">Painel Administrativo</h4>
                <p class="text-muted small mb-3">Gerencie as campanhas de arrecadação e as solicitações de saque da
                    plataforma</p>
            </div>

            <!-- NAVEGAÇÃO DE ABAS EM PÍLULA -->
            <div class="d-flex align-items-center gap-2 border-top pt-3 overflow-x-auto">
                <button
                    class="btn d-flex align-items-center gap-2 py-2 px-4 rounded-pill fw-medium transition-all border-0"
                    :class="activeTab === 'campaigns' ? 'btn-pmg-primary shadow-sm text-white' : 'text-secondary bg-light hover-bg-gray'"
                    @click="switchTab('campaigns')">
                    <i class="bi bi-megaphone"></i> Campanhas Arrecadatórias
                </button>
                <button class="btn d-flex align-items-center gap-2 py-2 px-4 rounded-pill fw-medium transition-all border-0 position-relative"
    :class="activeTab === 'withdrawals' ? 'btn-pmg-primary shadow-sm text-white' : 'text-secondary bg-light hover-bg-gray'"
    @click="switchTab('withdrawals')">
    <i class="bi bi-wallet2"></i> 
    Solicitações de Saque
    
    <!-- 👇 Badge de Contagem Pendente -->
    <span v-if="pendingWithdrawalsCount > 0" 
          class="badge rounded-pill ms-1 px-2 py-1"
          :class="activeTab === 'withdrawals' ? 'bg-white text-dark fw-bold' : 'bg-danger text-white'"
          style="font-size: 0.65rem;">
        {{ pendingWithdrawalsCount }}
    </span>
</button>
            </div>
        </div>

        <!-- ================= ABA 1: CAMPANHAS ================= -->
        <div v-if="activeTab === 'campaigns'" class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h5 class="mb-0 fw-bold">Campanhas Ativas</h5>
                    <span class="text-muted small">Exibindo {{ campaigns.length }} de {{ totalItems }} registros</span>
                </div>
                <div class="d-flex gap-2 align-items-center">
                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter, 'border-primary text-primary fw-bold': hasActiveFilters }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>
                    <button class="btn btn-pmg-light" @click="fetchCampaigns(1, false)" :disabled="loading"
                        title="Atualizar">
                        <i class="bi bi-arrow-clockwise" :class="{ 'spin-animation': loading }"></i>
                    </button>
                </div>
            </div>

            <!-- FILTROS DE CAMPANHAS -->
            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3 align-items-end">
                        <div class="col-md-4">
                            <label class="form-label text-muted small fw-bold mb-1">Busca por Nome</label>
                            <input v-model="filter.name" type="text" class="form-control form-control-pmg"
                                placeholder="Título da campanha...">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Criada De</label>
                            <input v-model="filter.date_from" type="date" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Criada Até</label>
                            <input v-model="filter.date_to" type="date" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Mín. Doações</label>
                            <input v-model="filter.min_donations" type="number" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Mín. Arrecadado</label>
                            <input v-model="filter.min_raised" type="number" class="form-control form-control-pmg">
                        </div>
                        <div class="col-12 d-flex justify-content-start gap-2 mt-3">
                            <button class="btn btn-pmg-primary fw-bold py-2 px-4" @click="fetchCampaigns(1, false)">
                                <i class="bi bi-search me-2"></i> Filtrar
                            </button>
                            <button class="btn btn-outline-danger py-2 px-3" @click="clearCampaignFilters">
                                <i class="bi bi-x-lg"></i> Limpar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TABELA DE CAMPANHAS -->
            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 25%">Campanha</th>
                            <th style="width: 15%">Criação</th>
                            <th style="width: 10%" class="text-center">Doações</th>
                            <th style="width: 15%" class="text-end">Bruto Arrecadado</th>
                            <th style="width: 12%" class="text-end">Lucro RMR</th>
                            <th style="width: 15%" class="text-end">Repasse Líquido</th>
                            <th style="width: 8%" class="text-end">Ações</th>
                        </tr>
                    </thead>
                    <tbody v-if="campaigns.length > 0">
                        <tr v-for="item in campaigns" :key="item.id">
                            <td>
                                <div class="fw-bold text-dark">{{ item.title }}</div>
                                <span class="text-muted small">/{{ item.slug }}</span>
                            </td>
                            <td>{{ item.created_at }}</td>
                            <td class="text-center">
                                <span class="badge bg-light text-dark border px-3 py-1 fs-6">{{
                                    item.total_donations_count }}</span>
                            </td>
                            <td class="text-end fw-bold text-success">R$ {{ formatCurrency(item.total_raised) }}</td>
                            <td class="text-end fw-bold" style="color: #561569;">R$ {{
                                formatCurrency(item.company_profit) }}</td>
                            <td class="text-end fw-bold text-primary">R$ {{ formatCurrency(item.net_transferred) }}</td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"><i
                                            class="bi bi-three-dots-vertical"></i></button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3">
                                        <li><a class="dropdown-item py-2 text-primary" :href="item.links.view_public"
                                                target="_blank"><i class="bi bi-box-arrow-up-right me-2"></i>Ver
                                                Página</a></li>
                                        <li><a class="dropdown-item py-2" href="#"
                                                @click.prevent="copyToClipboard(item.links.view_public)"><i
                                                    class="bi bi-copy me-2"></i>Copiar Link</a></li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="7" class="text-center py-5 text-muted"><i
                                    class="bi bi-megaphone fs-1 mb-2 opacity-50"></i><br>Nenhuma campanha encontrada.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMoreCampaigns">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMoreCampaigns"
                    :disabled="loading">Carregar mais</button>
            </div>
        </div>

        <!-- ================= ABA 2: SAQUES ================= -->
        <div v-if="activeTab === 'withdrawals'" class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h5 class="mb-0 fw-bold">Solicitações de Saque</h5>
                    <span class="text-muted small">Exibindo {{ withdrawals.length }} de {{ withdrawalTotalItems }}
                        registros</span>
                </div>
                <div class="d-flex gap-2 align-items-center">
                    <button class="btn btn-pmg-light" @click="showWithdrawalFilter = !showWithdrawalFilter"
                        :class="{ 'active': showWithdrawalFilter, 'border-primary text-primary fw-bold': hasActiveWithdrawalFilters }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>
                    <button class="btn btn-pmg-light" @click="fetchWithdrawals(1, false)" :disabled="withdrawalLoading"
                        title="Atualizar">
                        <i class="bi bi-arrow-clockwise" :class="{ 'spin-animation': withdrawalLoading }"></i>
                    </button>
                </div>
            </div>

            <!-- FILTROS DE SAQUES -->
            <div class="collapse" :class="{ 'show': showWithdrawalFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3 align-items-end">
                        <div class="col-md-3">
                            <label class="form-label text-muted small fw-bold mb-1">Nome do Cliente</label>
                            <input v-model="withdrawalFilter.client_name" type="text"
                                class="form-control form-control-pmg" placeholder="Nome do solicitante...">
                        </div>
                        <div class="col-md-3">
                            <label class="form-label text-muted small fw-bold mb-1">Documento (CPF/CNPJ)</label>
                            <input v-model="withdrawalFilter.client_document" type="text"
                                class="form-control form-control-pmg" placeholder="Apenas números...">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Status</label>
                            <select v-model="withdrawalFilter.status" class="form-select form-control-pmg">
                                <option value="">Todos</option>
                                <option value="pending">Pendente</option>
                                <option value="approved">Aprovado</option>
                                <option value="paid">Pago</option>
                                <option value="rejected">Rejeitado</option>
                            </select>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Mín. Valor (R$)</label>
                            <input v-model="withdrawalFilter.min_amount" type="number"
                                class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Máx. Valor (R$)</label>
                            <input v-model="withdrawalFilter.max_amount" type="number"
                                class="form-control form-control-pmg">
                        </div>
                        <div class="col-12 d-flex justify-content-start gap-2 mt-3">
                            <button class="btn btn-pmg-primary fw-bold py-2 px-4" @click="fetchWithdrawals(1, false)">
                                <i class="bi bi-search me-2"></i> Filtrar
                            </button>
                            <button class="btn btn-outline-danger py-2 px-3" @click="clearWithdrawalFilters">
                                <i class="bi bi-x-lg"></i> Limpar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TABELA DE SAQUES -->
            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 25%">Cliente / Solicitante</th>
                            <th style="width: 20%">Chave PIX</th>
                            <th style="width: 15%" class="text-end">Valor do Saque</th>
                            <th style="width: 15%" class="text-center">Status</th>
                            <th style="width: 15%">Data de Solicitação</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>
                    <tbody v-if="withdrawals.length > 0">
                        <tr v-for="item in withdrawals" :key="item.id">
                            <td>
                                <div class="fw-bold text-dark">{{ item.client?.name || 'Cliente Removido' }}</div>
                                <span class="text-muted small">Doc: {{ item.client?.document || 'N/D' }}</span>
                            </td>
                            <td>
                                <div class="fw-medium text-dark">{{ item.pix_key }}</div>
                                <span class="badge bg-light text-secondary border uppercase"
                                    style="font-size: 0.65rem;">
                                    {{ item.pix_type }}
                                </span>
                            </td>
                            <td class="text-end fw-bold text-dark">
                                R$ {{ formatCurrency(item.amount) }}
                            </td>
                            <td class="text-center">
                                <span class="badge rounded-pill px-3 py-2"
                                    :class="getWithdrawalStatusClass(item.status)">
                                    {{ getWithdrawalStatusLabel(item.status) }}
                                </span>
                            </td>
                            <td>
                                {{ formatDate(item.created_at) }}
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"><i
                                            class="bi bi-three-dots-vertical"></i></button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3">
                                        <li>
                                            <a class="dropdown-item py-2" href="#"
                                                @click.prevent="openWithdrawalDetails(item)">
                                                <i class="bi bi-eye text-primary me-2"></i> Ver Detalhes / Notas
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="6" class="text-center py-5 text-muted">
                                <i class="bi bi-wallet2 fs-1 mb-2 opacity-50"></i><br>
                                <span>Nenhuma solicitação de saque encontrada.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="withdrawalHasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMoreWithdrawals"
                    :disabled="withdrawalLoading">Carregar mais</button>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            activeTab: 'campaigns',
            alerts: [],

            // --- ESTADO DA ABA DE CAMPANHAS ---
            campaigns: [],
            filter: { name: '', date_from: '', date_to: '', min_donations: '', min_raised: '' },
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            per_page: 15,
            loading: false,
            hasMoreCampaigns: false,
            showFilter: false,

            // --- ESTADO DA ABA DE SAQUES ---
            withdrawals: [],
            withdrawalFilter: { status: '', min_amount: '', max_amount: '', client_name: '', client_document: '' },
            withdrawalTotalItems: 0,
            withdrawalCurrentPage: 1,
            withdrawalLastPage: 1,
            withdrawalPerPage: 15,
            withdrawalLoading: false,
            withdrawalHasMore: false,
            showWithdrawalFilter: false,
            pendingWithdrawalsCount: 0,
        }
    },
    computed: {
        hasActiveFilters() {
            return Object.values(this.filter).some(val => val !== '');
        },
        hasActiveWithdrawalFilters() {
            return Object.values(this.withdrawalFilter).some(val => val !== '');
        }
    },
    mounted() {
        this.fetchCampaigns();
        this.fetchPendingWithdrawalsCount();
    },
    methods: {
        switchTab(tab) {
            this.activeTab = tab;
            if (tab === 'campaigns' && this.campaigns.length === 0) {
                this.fetchCampaigns();
            } else if (tab === 'withdrawals' && this.withdrawals.length === 0) {
                this.fetchWithdrawals();
            }
        },

        // --- MÉTODOS DE CAMPANHAS ---
        async fetchCampaigns(page = 1, append = false) {
            if (this.globalLoading) this.globalLoading.show('Carregando campanhas...');
            this.loading = true;
            try {
                const response = await api.get('/admin-landing-page/index', {
                    params: { page, per_page: this.per_page, ...this.filter }
                });
                const { data, meta } = response.data;
                this.campaigns = append ? [...this.campaigns, ...data] : data;
                this.totalItems = meta.total;
                this.currentPage = meta.current_page;
                this.lastPage = meta.last_page;
                this.hasMoreCampaigns = this.currentPage < this.lastPage;
            } catch (e) {
                this.showAlert('Erro ao buscar as campanhas.', 'error');
            } finally {
                this.loading = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        },
        async fetchPendingWithdrawalsCount() {
        try {
            const response = await api.get('/admin-landing-page/withdrawals', {
                params: { status: 'pending', per_page: 1 }
            });
            // O Laravel retorna o total da paginação direto na raiz do objeto JSON
            this.pendingWithdrawalsCount = response.data.total || 0;
        } catch (e) {
            console.error("Erro ao buscar contagem de saques pendentes", e);
        }
    },
        clearCampaignFilters() {
            this.filter = { name: '', date_from: '', date_to: '', min_donations: '', min_raised: '' };
            this.fetchCampaigns(1, false);
        },
        loadMoreCampaigns() {
            if (this.hasMoreCampaigns) this.fetchCampaigns(this.currentPage + 1, true);
        },

        // --- MÉTODOS DE SAQUES ---
        async fetchWithdrawals(page = 1, append = false) {
            if (this.globalLoading) this.globalLoading.show('Carregando saques...');
            this.withdrawalLoading = true;
            try {
                const response = await api.get('/admin-landing-page/withdrawals', {
                    params: { page, per_page: this.withdrawalPerPage, ...this.withdrawalFilter }
                });

                // Como o Laravel retorna a paginação pura, lemos direto de response.data
                const pagedData = response.data;
                const items = pagedData.data || [];

                this.withdrawals = append ? [...this.withdrawals, ...items] : items;
                this.withdrawalTotalItems = pagedData.total || 0;
                this.withdrawalCurrentPage = pagedData.current_page || 1;
                this.withdrawalLastPage = pagedData.last_page || 1;
                this.withdrawalHasMore = this.withdrawalCurrentPage < this.withdrawalLastPage;
            } catch (e) {
                console.error(e);
                this.showAlert('Erro ao buscar solicitações de saque.', 'error');
            } finally {
                this.withdrawalLoading = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        },
        clearWithdrawalFilters() {
            this.withdrawalFilter = { status: '', min_amount: '', max_amount: '', client_name: '', client_document: '' };
            this.fetchWithdrawals(1, false);
        },
        loadMoreWithdrawals() {
            if (this.withdrawalHasMore) this.fetchWithdrawals(this.withdrawalCurrentPage + 1, true);
        },

        getWithdrawalStatusLabel(status) {
            return {
                pending: 'Pendente',
                approved: 'Aprovado',
                paid: 'Pago',
                rejected: 'Rejeitado'
            }[status] || status;
        },
        getWithdrawalStatusClass(status) {
            return {
                pending: 'bg-warning-subtle text-warning',
                approved: 'bg-info-subtle text-info border border-info-subtle',
                paid: 'bg-success-subtle text-success',
                rejected: 'bg-danger-subtle text-danger'
            }[status] || 'bg-light';
        },
        openWithdrawalDetails(item) {
            this.showAlert(`Visualizando saque de R$ ${this.formatCurrency(item.amount)}`, 'success');
        },

        // --- UTILITÁRIOS ---
        formatCurrency(v) {
            return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleDateString('pt-BR') : '-';
        },
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.showAlert('Link copiado com sucesso!', 'success');
            } catch (err) {
                this.showAlert('Não foi possível copiar o link.', 'warning');
            }
        },
        showAlert(message, type = 'success') {
            const id = Date.now();
            const themeClass = type === 'error' ? 'danger' : type;
            let icon = 'bi-check-circle-fill';
            if (themeClass === 'danger') icon = 'bi-x-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            this.alerts.push({ id, message, type: themeClass, icon });
            setTimeout(() => this.removeAlert(id), 4000);
        },
        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        }
    }
}
</script>

<style scoped>
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 50px;
    /* Estilo pílula consistente */
    padding: 8px 20px;
    font-weight: 500;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.hover-bg-gray:hover {
    background-color: #e2e8f0 !important;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.transition-all {
    transition: all 0.2s ease;
}
</style>